import { advanceWhile, type FieldPathWalkContext } from "./walk-field-path";
import { getNextField } from "./get-next-field";
import type { FormFieldNode } from "./types";

/**
 * Ids to un-mark as navigated when stepping back from `activeField` to `previousField`.
 *
 * Covers the field being left plus any `Hidden Value` fields that sit between the two,
 * since those were auto-included on the way forward. Dropping them means that if the
 * enduser changes their answer and takes a different branch, the hidden fields from the
 * abandoned branch aren't submitted.
 */
export const collectIdsToDropOnBack = (
  activeField: FormFieldNode | undefined,
  previousField: FormFieldNode | undefined,
  walkContext: FieldPathWalkContext
): Set<string> => {
  const idsToDrop = new Set<string>();
  if (activeField) idsToDrop.add(activeField.value.id);
  if (!previousField) return idsToDrop;

  const first = getNextField(
    previousField,
    walkContext.responsesByFieldId[previousField.value.id],
    walkContext.allResponses,
    walkContext.logicOptions
  );
  if (!first) return idsToDrop;

  const { node, skipped } = advanceWhile(first, (c) => c.value.type === "Hidden Value", walkContext);
  skipped.forEach((c) => idsToDrop.add(c.value.id));
  if (node.value.type === "Hidden Value") idsToDrop.add(node.value.id);

  return idsToDrop;
};
